import React, { useState } from 'react';
import { Search, Heart, MessageCircle, Gift, ChevronDown, CheckCircle, Filter, X, Sparkles, Trophy, Clock, Star, Eye } from 'lucide-react';
type FavoriteType = 'angel' | 'skin' | 'post' | 'video' | 'premium';
interface Favorite {
  id: string;
  type: FavoriteType;
  title: string;
  creatorName: string;
  creatorVerified: boolean;
  thumbnail: string;
  likes: number;
  views?: number;
  bondLevel?: number;
  isPremium?: boolean;
  dateAdded: Date;
}
export const FavoritesSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'angels' | 'skins' | 'posts' | 'videos' | 'premium'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'most-interacted' | 'by-category'>('newest');
  // Sample data for favorites
  const [favorites, setFavorites] = useState<Favorite[]>([{
    id: '1',
    type: 'angel',
    title: 'Your Naughty Angel',
    creatorName: 'GoldenShowerTina',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
    likes: 1245,
    bondLevel: 4,
    dateAdded: new Date('2023-10-15')
  }, {
    id: '2',
    type: 'video',
    title: 'Private Dance Just For You',
    creatorName: 'Bonny Blue',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
    likes: 872,
    views: 12400,
    isPremium: true,
    dateAdded: new Date('2023-11-02')
  }, {
    id: '3',
    type: 'skin',
    title: 'Midnight Temptress Skin',
    creatorName: 'Harper',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
    likes: 1872,
    bondLevel: 2,
    isPremium: true,
    dateAdded: new Date('2023-12-01')
  }, {
    id: '4',
    type: 'post',
    title: 'Just thinking about you...',
    creatorName: 'Kim Holland',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
    likes: 2145,
    views: 8700,
    dateAdded: new Date('2023-12-15')
  }, {
    id: '5',
    type: 'angel',
    title: 'Your Fantasy Girl',
    creatorName: 'Mia Khalifa',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg',
    likes: 5621,
    bondLevel: 3,
    dateAdded: new Date('2023-09-28')
  }, {
    id: '6',
    type: 'premium',
    title: 'Exclusive After Hours Stream',
    creatorName: 'Lana',
    creatorVerified: true,
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
    likes: 982,
    views: 3400,
    isPremium: true,
    dateAdded: new Date('2023-12-20')
  }]);
  // Filter favorites based on active category and search query
  const filteredFavorites = favorites.filter(fav => {
    const matchesCategory = activeCategory === 'all' || activeCategory === 'premium' && fav.isPremium || fav.type === activeCategory.slice(0, -1); // Remove 's' from end
    const matchesSearch = searchQuery === '' || fav.title.toLowerCase().includes(searchQuery.toLowerCase()) || fav.creatorName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  // Sort favorites based on selected order
  const sortedFavorites = [...filteredFavorites].sort((a, b) => {
    if (sortOrder === 'newest') {
      return b.dateAdded.getTime() - a.dateAdded.getTime();
    } else if (sortOrder === 'most-interacted') {
      return b.likes + (b.views || 0) - (a.likes + (a.views || 0));
    } else {
      // by-category
      return a.type.localeCompare(b.type);
    }
  });
  const handleRemoveFavorite = (id: string) => {
    setFavorites(favorites.filter(fav => fav.id !== id));
  };
  // Calculate user's favorite stats
  const totalAngels = favorites.filter(f => f.type === 'angel').length;
  const premiumCount = favorites.filter(f => f.isPremium).length;
  const topBondLevel = Math.max(...favorites.map(f => f.bondLevel || 0), 0);
  return <div className="w-full bg-white dark:bg-gray-950 min-h-[80vh]" data-id="element-7528">
      {/* Header Section */}
      <div className="relative mb-6" data-id="element-7529">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl" data-id="element-7530" />
        <div className="relative p-6 rounded-xl" data-id="element-7531">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-7532">
            <div data-id="element-7533">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-7534">
                <Heart size={24} className="text-pink-500 fill-pink-500 animate-pulse-subtle" data-id="element-7535" />
                My Favorites
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1 max-w-2xl" data-id="element-7536">
                Your handpicked Angels, posts, and experiences — all in one
                place.
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-7537">
              <div className="bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5" data-id="element-7538">
                <Trophy size={14} className="text-amber-500" data-id="element-7539" />
                <span data-id="element-7540">Bond Level {topBondLevel}</span>
              </div>
              {premiumCount > 0 && <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 dark:from-amber-600/20 dark:to-yellow-600/20 border border-amber-200 dark:border-amber-800/30 px-3 py-1.5 rounded-full text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1.5" data-id="element-7541">
                  <Sparkles size={14} className="text-amber-500" data-id="element-7542" />
                  <span data-id="element-7543">{premiumCount} Premium</span>
                </div>}
            </div>
          </div>
          {/* Search and Sort Controls */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3" data-id="element-7544">
            <div className="relative flex-1" data-id="element-7545">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-7546" />
              <input type="text" placeholder="Search your favorites..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-600" data-id="element-7547" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-7548">
                  <X size={16} data-id="element-7549" />
                </button>}
            </div>
            <div className="relative" data-id="element-7550">
              <div className="flex items-center" data-id="element-7551">
                <div className="relative inline-block" data-id="element-7552">
                  <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-600" data-id="element-7553">
                    <Filter size={16} data-id="element-7554" />
                    <span data-id="element-7555">
                      {sortOrder === 'newest' ? 'Newest' : sortOrder === 'most-interacted' ? 'Most Interacted' : 'By Category'}
                    </span>
                    <ChevronDown size={16} data-id="element-7556" />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-10 hidden" data-id="element-7557">
                    <button onClick={() => setSortOrder('newest')} className="w-full px-4 py-2 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2" data-id="element-7558">
                      <Clock size={16} data-id="element-7559" />
                      <span data-id="element-7560">Newest</span>
                    </button>
                    <button onClick={() => setSortOrder('most-interacted')} className="w-full px-4 py-2 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2" data-id="element-7561">
                      <Star size={16} data-id="element-7562" />
                      <span data-id="element-7563">Most Interacted</span>
                    </button>
                    <button onClick={() => setSortOrder('by-category')} className="w-full px-4 py-2 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2" data-id="element-7564">
                      <Filter size={16} data-id="element-7565" />
                      <span data-id="element-7566">By Category</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Categories/Tabs */}
      <div className="flex overflow-x-auto scrollbar-hide mb-6 pb-2" data-id="element-7567">
        <div className="flex space-x-2 mx-auto" data-id="element-7568">
          <button onClick={() => setActiveCategory('all')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeCategory === 'all' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-7569">
            All Favorites
          </button>
          <button onClick={() => setActiveCategory('angels')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeCategory === 'angels' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-7570">
            Angels
          </button>
          <button onClick={() => setActiveCategory('skins')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeCategory === 'skins' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-7571">
            Skins
          </button>
          <button onClick={() => setActiveCategory('posts')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeCategory === 'posts' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-7572">
            Posts
          </button>
          <button onClick={() => setActiveCategory('videos')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeCategory === 'videos' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-7573">
            Videos
          </button>
          {premiumCount > 0 && <button onClick={() => setActiveCategory('premium')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1 ${activeCategory === 'premium' ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md' : 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-200 dark:border-amber-800/30 text-amber-700 dark:text-amber-300 hover:from-amber-500/20 hover:to-yellow-500/20'}`} data-id="element-7574">
              <Sparkles size={14} data-id="element-7575" />
              Premium
            </button>}
        </div>
      </div>
      {/* Favorites Grid */}
      {sortedFavorites.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-7576">
          {sortedFavorites.map(favorite => <div key={favorite.id} className="group relative rounded-xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300" data-id="element-7577">
              <div className="relative aspect-[4/3] overflow-hidden" data-id="element-7578">
                <img src={favorite.thumbnail} alt={favorite.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-7579" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-80" data-id="element-7580" />
                {/* Type Badge */}
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-black/30 backdrop-blur-sm text-white border border-white/20" data-id="element-7581">
                  {favorite.type === 'angel' && 'Angel'}
                  {favorite.type === 'skin' && 'Skin'}
                  {favorite.type === 'post' && 'Post'}
                  {favorite.type === 'video' && 'Video'}
                  {favorite.type === 'premium' && 'Premium'}
                </div>
                {/* Premium Badge */}
                {favorite.isPremium && <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500 to-yellow-500 text-white flex items-center gap-1" data-id="element-7582">
                    <Sparkles size={12} data-id="element-7583" />
                    Premium
                  </div>}
                {/* Bond Level */}
                {favorite.bondLevel && <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-pink-500/80 backdrop-blur-sm text-white flex items-center gap-1" data-id="element-7584">
                    <Heart size={12} fill="white" data-id="element-7585" />
                    Bond Lvl {favorite.bondLevel}
                  </div>}
                {/* Creator Info */}
                <div className="absolute bottom-3 left-3 flex items-center gap-2" data-id="element-7586">
                  <div className="text-white text-sm font-medium flex items-center gap-1" data-id="element-7587">
                    {favorite.creatorName}
                    {favorite.creatorVerified && <CheckCircle size={14} className="text-blue-400" fill="currentColor" data-id="element-7588" />}
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-7589">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-1" data-id="element-7590">
                  {favorite.title}
                </h3>
                {/* Stats */}
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-3" data-id="element-7591">
                  <div className="flex items-center gap-1" data-id="element-7592">
                    <Heart size={14} className="text-pink-500" data-id="element-7593" />
                    {favorite.likes.toLocaleString()}
                  </div>
                  {favorite.views && <div className="flex items-center gap-1" data-id="element-7594">
                      <Eye size={14} data-id="element-7595" />
                      {favorite.views.toLocaleString()}
                    </div>}
                </div>
                {/* Action Buttons */}
                <div className="flex items-center gap-2" data-id="element-7596">
                  <button className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5 text-gray-800 dark:text-gray-200" data-id="element-7597">
                    <MessageCircle size={16} data-id="element-7598" />
                    <span data-id="element-7599">Chat</span>
                  </button>
                  <button className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5 text-gray-800 dark:text-gray-200" data-id="element-7600">
                    <Gift size={16} data-id="element-7601" />
                    <span data-id="element-7602">Gift</span>
                  </button>
                  <button onClick={() => handleRemoveFavorite(favorite.id)} className="py-2 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors flex items-center justify-center text-gray-800 dark:text-gray-200" data-id="element-7603">
                    <Heart size={16} className="text-pink-500 fill-pink-500" data-id="element-7604" />
                  </button>
                </div>
              </div>
            </div>)}
        </div> :
    // Empty State
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-id="element-7605">
          <div className="w-40 h-40 mb-6 relative" data-id="element-7606">
            <div className="absolute inset-0 bg-pink-500/10 dark:bg-pink-500/5 rounded-full animate-pulse" data-id="element-7607" />
            <Heart size={80} className="absolute inset-0 m-auto text-pink-500/50" data-id="element-7608" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-7609">
            You haven't chosen your favorites yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mb-6" data-id="element-7610">
            Show your love by tapping the ❤️ icon on content you adore — Angels
            notice when you do and may reward your affection!
          </p>
          <button className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-medium shadow-md hover:shadow-lg hover:from-pink-600 hover:to-purple-600 transition-all" data-id="element-7611">
            Browse Angels
          </button>
        </div>}
      {/* Loyalty Rewards Banner */}
      {favorites.length > 0 && <div className="mt-10 mb-6 p-4 bg-gradient-to-r from-purple-600/10 to-pink-600/10 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200/30 dark:border-purple-800/30" data-id="element-7612">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4" data-id="element-7613">
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 dark:from-purple-500/30 dark:to-pink-500/30 p-3 rounded-full" data-id="element-7614">
              <Trophy size={24} className="text-pink-500" data-id="element-7615" />
            </div>
            <div className="flex-1" data-id="element-7616">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-7617">
                ❤️ Loyalty Rewards
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm" data-id="element-7618">
                Favoriting the same Angel multiple times increases your Bond
                Meter, unlocking special perks and exclusive content!
              </p>
            </div>
            <button className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all whitespace-nowrap" data-id="element-7619">
              View Rewards
            </button>
          </div>
        </div>}
      {/* Upgrade Banner (if close to limit) */}
      {favorites.length >= 5 && <div className="mt-6 p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 dark:from-amber-600/20 dark:to-yellow-600/20 rounded-xl border border-amber-200/30 dark:border-amber-800/30" data-id="element-7620">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4" data-id="element-7621">
            <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 dark:from-amber-500/30 dark:to-yellow-500/30 p-3 rounded-full" data-id="element-7622">
              <Sparkles size={24} className="text-amber-500" data-id="element-7623" />
            </div>
            <div className="flex-1" data-id="element-7624">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-7625">
                💎 Premium Favorites
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm" data-id="element-7626">
                You've used 6 of 10 free favorite slots. Upgrade to VIP for
                unlimited favorites and premium content!
              </p>
            </div>
            <button className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all whitespace-nowrap" data-id="element-7627">
              Upgrade Now
            </button>
          </div>
        </div>}
    </div>;
};